import createCachedSelector from 're-reselect'
import { createSelector } from 'reselect'
import { createBasicSelector } from 'util/redux'
import { emptyObj, isEmpty } from 'util/objects'
import { emptyArr, groupByToMap } from 'util/arrays'
import { selectRequestByType } from 'ducks/requests/selectors'
import {
  DRAFT_ORDER_CALCULATE_REQUEST,
  DRAFT_ORDER_COMPLETE_REQUEST,
  FETCH_CUSTOMER_ID_REQUEST,
} from '../types'

const byIntegrationIdCacheKey = (_state, integrationId) =>
  integrationId || 'unknown'

const byCustomerIdCacheKey = (_state, customerId) => customerId || 'unknown'

export const selectIntegrationData = state =>
  state?.integrations?.thirdPartyData?.shopify || emptyObj

const selectShopInfo = createBasicSelector(
  selectIntegrationData,
  integrationData => integrationData.shop || emptyObj
)

const selectShopInfoByIntegrationId = createBasicSelector(
  selectShopInfo,
  shopInfo => shopInfo.byIntegrationId || emptyObj
)

const selectShopInfoForIntegrationId = createBasicSelector(
  selectShopInfoByIntegrationId,
  (_state, integrationId) => integrationId,
  (shopInfoByIntegrationId, integrationId) =>
    shopInfoByIntegrationId[integrationId] || emptyObj
)

const selectDefaultCurrencyCode = createBasicSelector(
  selectShopInfo,
  shopInfo => shopInfo.defaultCurrencyCode
)

export const selectCurrencyCode = createBasicSelector(
  selectShopInfoForIntegrationId,
  selectDefaultCurrencyCode,
  (shop, defaultCurrencyCode) => shop?.currencyCode || defaultCurrencyCode
)

export const selectContactEmail = createBasicSelector(
  selectShopInfoForIntegrationId,
  shop => shop.contactEmail
)

export const selectShopIsLoading = createBasicSelector(
  selectShopInfoForIntegrationId,
  shop => shop.isLoading
)

export const selectShopIsLoaded = createBasicSelector(
  selectShopInfoForIntegrationId,
  shop => shop.loaded
)

export const selectDraftOrders = createBasicSelector(
  selectIntegrationData,
  integrationData => integrationData.draftOrders
)

export const selectDraftOrdersById = createBasicSelector(
  selectDraftOrders,
  draftOrders => draftOrders.byId || emptyObj
)

export const selectDraftOrderForId = createBasicSelector(
  selectDraftOrdersById,
  (_state, draftOrderId) => draftOrderId,
  (draftOrdersById, draftOrderId) => draftOrdersById[draftOrderId] || emptyObj
)

export const selectDraftOrdersByCustomerId = createBasicSelector(
  selectDraftOrders,
  draftOrders => draftOrders.byCustomerId || emptyObj
)

export const selectDraftOrderForCustomerId = createBasicSelector(
  selectDraftOrdersByCustomerId,
  selectDraftOrdersById,
  (state, { customerId }) => customerId,
  (byCustomerId, byId, customerId) => {
    const draftOrderId = byCustomerId[customerId]
    if (!draftOrderId) return null

    return byId[draftOrderId]
  }
)

export const selectDraftOrderHasLineItemsForId = createBasicSelector(
  selectDraftOrderForId,
  draftOrder => {
    if (isEmpty(draftOrder)) return false

    return draftOrder?.lineItems?.length > 0
  }
)

const selectProducts = createBasicSelector(
  selectIntegrationData,
  integrationData => integrationData.products
)

const selectProductsByIntegrationId = createBasicSelector(
  selectProducts,
  products => products.byIntegrationId || emptyObj
)

const selectProductsForIntegrationId = createBasicSelector(
  selectProductsByIntegrationId,
  (_state, integrationId) => integrationId,
  (productsByIntegrationId, integrationId) =>
    productsByIntegrationId[integrationId] || emptyObj
)

const selectProductsByQuery = createBasicSelector(
  selectProductsForIntegrationId,
  products => products.byQuery || emptyObj
)

const selectProductsById = createBasicSelector(
  selectProductsForIntegrationId,
  products => products.byId || emptyObj
)

export const selectProductsIsLoading = createBasicSelector(
  selectProductsForIntegrationId,
  products => products.isLoading
)

export const selectProductsForQuery = createCachedSelector(
  selectProductsByQuery,
  selectProductsById,
  (state, integrationId) => integrationId,
  (state, _integrationId, { query }) => query,
  (byQuery, byId, _integrationId, query) => {
    const productIds = byQuery[query] || emptyArr
    return productIds.map(productId => byId[productId])
  }
)(
  (state, integrationId, { query }) =>
    `${byIntegrationIdCacheKey(state, integrationId)}-${query || 'unknown'}`
)

export const selectIsLoadedForQuery = createBasicSelector(
  selectProductsByQuery,
  (state, integrationId) => integrationId,
  (state, _integrationId, { query }) => query,
  (byQuery, _integrationId, query) => {
    return !!byQuery[query]
  }
)

export const selectOrders = createBasicSelector(
  selectIntegrationData,
  integrationData => integrationData.orders
)

export const selectOrdersLoading = createBasicSelector(
  selectOrders,
  orders => orders.isLoading
)

export const selectOrdersLoaded = createBasicSelector(
  selectOrders,
  orders => orders.isLoaded
)

export const selectOrdersByCustomerId = createBasicSelector(
  selectOrders,
  orders => orders.byCustomerId || emptyObj
)

export const selectEvents = createBasicSelector(
  selectIntegrationData,
  integrationData => integrationData.events
)

export const selectCustomers = createBasicSelector(
  selectIntegrationData,
  integrationData => integrationData.customers || emptyObj
)

export const selectEventsCustomerIdsByIntegrationId = createBasicSelector(
  selectEvents,
  events => events.customerIdsByIntegrationId || emptyObj
)

export const selectEventsCustomerIdsForIntegrationId = createBasicSelector(
  selectEventsCustomerIdsByIntegrationId,
  (state, integrationId) => integrationId,
  (customerIdsForIntegrationId, integrationId) =>
    customerIdsForIntegrationId[integrationId] || emptyArr
)

export const selectFetchCustomerIdRequest = state => {
  return selectRequestByType(state, FETCH_CUSTOMER_ID_REQUEST)
}

export const selectEventsMetaByCustomerId = createBasicSelector(
  selectEvents,
  events => events.metaByCustomerId || emptyObj
)

export const selectEventsIsLoadingForIntegrationId = createCachedSelector(
  selectEventsCustomerIdsForIntegrationId,
  selectEventsMetaByCustomerId,
  (eventsCustomerIdsForIntegrationId, metaByCustomerId) => {
    const loadingStates = []

    eventsCustomerIdsForIntegrationId.forEach(customerId => {
      loadingStates.push(metaByCustomerId[customerId]?.isLoading || false)
    })

    return loadingStates.some(loading => loading)
  }
)(byIntegrationIdCacheKey)

export const selectIsLoading = createBasicSelector(
  selectOrdersLoading,
  selectOrdersLoaded,
  selectEventsIsLoadingForIntegrationId,
  selectShopIsLoading,
  selectFetchCustomerIdRequest,
  (
    ordersLoading,
    ordersLoaded,
    eventsLoading,
    shopLoading,
    { loading: fetchCustomerIdLoading = false }
  ) =>
    (ordersLoading && !ordersLoaded) ||
    eventsLoading ||
    shopLoading ||
    fetchCustomerIdLoading
)

export const selectEventsByCustomerId = createBasicSelector(
  selectEvents,
  events => events.byCustomerId || emptyObj
)

export const selectEventsForCustomerId = createBasicSelector(
  selectEventsByCustomerId,
  (state, customerId) => customerId,
  (byCustomerId, customerId) => {
    const forCustomerId = byCustomerId[customerId]
    return forCustomerId
  }
)

export const selectEventsMetaForCustomerId = createBasicSelector(
  selectEventsMetaByCustomerId,
  (state, customerId) => customerId,
  (metaByCustomerId, customerId) => {
    const forCustomerId = metaByCustomerId[customerId]
    return forCustomerId
  }
)

export const selectEventsForCustomerIdGroupedByDay = createCachedSelector(
  selectEventsByCustomerId,
  (state, customerId) => customerId,
  (byCustomerId, customerId) => {
    const forCustomerId = byCustomerId[customerId] || []
    return groupByToMap(forCustomerId, ({ date }) => date)
  }
)(byCustomerIdCacheKey)

export const selectOrdersForCustomerId = createBasicSelector(
  selectOrdersByCustomerId,
  (state, customerId) => customerId,
  (byCustomerId, customerId) => {
    const forCustomerId = byCustomerId[customerId]
    return forCustomerId
  }
)

export const selectOrdersById = createSelector(
  selectOrdersByCustomerId,
  byCustomerId => {
    const byId = {}
    Object.keys(byCustomerId).forEach(customerId => {
      Object.values(byCustomerId[customerId]).forEach(order => {
        byId[order.id] = order
      })
    })
    return byId
  }
)

export const selectOrderForId = createBasicSelector(
  selectOrdersById,
  (state, orderId) => orderId,
  (byId, orderId) => {
    return byId[orderId]
  }
)

const selectPaymentTermTemplates = createBasicSelector(
  selectIntegrationData,
  integrationData => integrationData.paymentTermsTemplates || emptyObj
)

const selectPaymentTermTemplatesByIntegrationId = createBasicSelector(
  selectPaymentTermTemplates,
  paymentTermTemplates => paymentTermTemplates.byIntegrationId || emptyObj
)

export const selectPaymentTermTemplatesIsLoading = createBasicSelector(
  selectPaymentTermTemplatesByIntegrationId,
  (_state, integrationId) => integrationId,
  (paymentTermTemplatesByIntegrationId, integrationId) =>
    paymentTermTemplatesByIntegrationId[integrationId]?.isLoading
)

export const selectPaymentTermTemplatesIsLoaded = createBasicSelector(
  selectPaymentTermTemplatesByIntegrationId,
  (_state, integrationId) => integrationId,
  (paymentTermTemplatesByIntegrationId, integrationId) =>
    paymentTermTemplatesByIntegrationId[integrationId]?.loaded || false
)

export const selectPaymentTermTemplatesHasError = createBasicSelector(
  selectPaymentTermTemplatesByIntegrationId,
  (_state, integrationId) => integrationId,
  (paymentTermTemplatesByIntegrationId, integrationId) =>
    paymentTermTemplatesByIntegrationId[integrationId]?.error || false
)

export const selectPaymentTermTemplatesForIntegrationId = createBasicSelector(
  selectPaymentTermTemplatesByIntegrationId,
  (_state, integrationId) => integrationId,
  (paymentTermTemplatesByIntegrationId, integrationId) =>
    paymentTermTemplatesByIntegrationId[integrationId]?.templates || emptyArr
)

export const selectDraftOrderCalculateRequest = state => {
  return selectRequestByType(state, DRAFT_ORDER_CALCULATE_REQUEST)
}

export const selectDraftOrderCompleteRequest = state => {
  return selectRequestByType(state, DRAFT_ORDER_COMPLETE_REQUEST)
}

export const selectCustomerIdByIntegrationIdAndEmail = createCachedSelector(
  selectCustomers,
  (_state, integrationId) => integrationId,
  (_state, _integrationId, email) => email,
  (customers, integrationId, email) => {
    const key = `${integrationId}-${email}`

    if (
      customers.byIntegrationIdAndEmail &&
      customers.byIntegrationIdAndEmail[key]
    ) {
      return customers.byIntegrationIdAndEmail[key]
    }

    return {
      found: false,
      integrationId,
      loaded: false,
    }
  }
)(
  (_state, integrationId, email) =>
    `${integrationId || 'unknown'}-${email || 'unknown'}`
)

export const selectOrdersMetaByCustomerId = createBasicSelector(
  selectOrders,
  orders => orders.metaByCustomerId || emptyObj
)

export const selectOrdersMetaForCustomerId = createBasicSelector(
  selectOrdersMetaByCustomerId,
  (_state, customerId) => customerId,
  (metaByCustomerId, customerId) => {
    const forCustomerId = metaByCustomerId[customerId]
    return forCustomerId || emptyObj
  }
)
